import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
} from "@mui/material";
import { Add, Delete, Download, FilterList, MoreVert, Search, FirstPage, LastPage, Close, PhotoCamera, Remove } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { TrashOutline } from "react-ionicons";
import "../../../shared/components/Item.css";
import MapPinLocation from "../../../shared/components/MapPinLocation.tsx";

import { useNavigate } from "react-router-dom";

import {
  OfficeSearchParams,
  getOffices,
  createOffice,
  uploadOfficePhoto,
  editOffice,
  fetchOfficePhotoBlob,
  deleteOffice,
  createOfficeLocation,
  getOfficeLocations,
  // Tambahkan service attachments
  uploadOfficeAttachments,
  getOfficeAttachments,
  downloadAttachmentBlob,
  downloadAttachmentFile,
  deleteAttachment,
  OfficeAttachment
} from "../services/officeService.tsx";
import { LocationSearchParams, getLocations, createLocation, editLocation, deleteLocation } from "../services/locationService.tsx";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import LoadingOverlay from "../../../shared/components/LoadingOverlay.tsx";

// Dummy Data
const officeTypes = ["PST", "MB", "PJTK", "KON", "SK"];

const officeTypeColors = {
  "PST": "green",
  "MB": "lightgreen",
  "PJTK": "	#164396",
  "KON": "gray",
  "SK": "orange"
};

const regions = ["Jamali", "Sulmapana", "Sumkal"];

const OfficeListPage: React.FC = () => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // Total semua data
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddOfficeDialog, setOpenAddOfficeDialog] = useState(false);
  const [openAddItemDetailsDialog, setOpenAddItemDetailsDialog] = useState(false);

  const [error, setError] = useState("");
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [filters, setFilters] = useState([{ field: "name", value: "" }]);

  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // State untuk attachments
  const [attachments, setAttachments] = useState<File[]>([]);
  // const [existingAttachments, setExistingAttachments] = useState<OfficeAttachment[]>([]);
  const [attachmentDescription, setAttachmentDescription] = useState("");

  // State untuk attachments - DIPISAH antara create dan edit
  const [createAttachments, setCreateAttachments] = useState<File[]>([]);
  const [createAttachmentDescription, setCreateAttachmentDescription] = useState("");

  const [editAttachments, setEditAttachments] = useState<File[]>([]);
  const [editAttachmentDescription, setEditAttachmentDescription] = useState("");
  const [existingAttachments, setExistingAttachments] = useState<OfficeAttachment[]>([]);

  const [itemsToDelete, setItemsToDelete] = useState<any[]>([]);


  const offset = (currentPage - 1) * rowsPerPage;

  const [sortConfig, setSortConfig] = useState<{ orderBy: string; order: "asc" | "desc" }>({
    orderBy: "createdAt",
    order: "desc",
  });

  const showPopupMandatoryError = () => {
    Swal.fire({
      icon: "error",
      title: "Data Belum Lengkap",
      text: "Lengkapi data yang wajib diisi.",
    });
  };

  const handleSort = (column: string) => {
    setSortConfig((prev) => {
      if (prev.orderBy === column) {
        return {
          orderBy: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          orderBy: column,
          order: "asc",
        };
      }
    });
    setCurrentPage(1); // reset ke halaman 1 saat sort berubah
  };


  // Daftar pilihan filter yang tersedia
  const availableFilters = [
    { value: "officeName", label: "Office Name" },
    { value: "officeAddress", label: "Office Address" },
    { value: "officeEmail", label: "Office Email" },
    { value: "officeType", label: "Office Type" },
    { value: "region", label: "Region" },
  ];

  // Daftar pilihan filter yang tersedia
  const availableFiltersInit = [
    { value: "officeName", label: "Office Name" },
    { value: "officeAddress", label: "Office Address" },
    { value: "officeEmail", label: "Office Email" },
    { value: "officeType", label: "Office Type" },
    { value: "region", label: "Region" },
  ];

  const [filterParams, setFilterParams] = useState<{
    keyword?: string;
    officeName?: string;
    officeAddress?: string;
    region?: string;
    officeType?: string;
    officeEmail?: string;
    managerName?: string;
    contactNumber?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    orderBy?: string;
    order?: string;
  }>({});

  const displayedRows = rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const fetchOffices = async () => {
    setIsLoading(true);
    try {


      const result = await getOffices({
        ...filterParams,
        limit: rowsPerPage,
        offset,
        orderBy: sortConfig.orderBy,
        order: sortConfig.order,
      });


      setRows(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {

    fetchOffices();
  }, [currentPage, rowsPerPage, filterParams, sortConfig]);

  const [officeData, setOfficeData] = useState<{
    officeId: string,
    officeName: string,
    officeAddress: string,
    latitude: string,
    longitude: string,
    region: string,
    province: string,
    city: string,
    district: string,
    managerName: string,
    contactNumber: string,
    officeEmail: string,
    officeType: string,
    file: File | null;
  }>({
    officeId: "",
    officeName: "",
    officeAddress: "",
    latitude: "",
    longitude: "",
    region: "",
    province: "",
    city: "",
    district: "",
    managerName: "",
    contactNumber: "",
    officeEmail: "",
    officeType: "",
    file: null,
  });

  const [errors, setErrors] = useState({
    officeId: false,
    officeName: false,
    officeAddress: false,
    latitude: false,
    longitude: false,
    region: false,
    province: false,
    city: false,
    district: false,
    managerName: false,
    contactNumber: false,
    officeEmail: false,
    officeType: false,
  });

  const [openEditOfficeDialog, setOpenEditOfficeDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editOfficeData, setEditOfficeData] = useState<{
    id: string,
    locationId: string,
    officeId: string,
    officeName: string,
    officeAddress: string,
    latitude: string,
    longitude: string,
    region: string,
    province: string,
    city: string,
    district: string,
    managerName: string,
    contactNumber: string,
    officeEmail: string,
    officeType: string,
    file: File | null;
    photoOffice: string;
    previewUrl: string;
  }>({
    id: "",
    locationId: "",
    officeId: "",
    officeName: "",
    officeAddress: "",
    latitude: "",
    longitude: "",
    region: "",
    province: "",
    city: "",
    district: "",
    managerName: "",
    contactNumber: "",
    officeEmail: "",
    officeType: "",
    file: null,
    photoOffice: "",
    previewUrl: "",
  });

  const [originalEditOfficeData, setOriginalEditOfficeData] = useState<{
    id: string,
    locationId: string,
    officeId: string,
    officeName: string,
    officeAddress: string,
    latitude: string,
    longitude: string,
    region: string,
    province: string,
    city: string,
    district: string,
    managerName: string,
    contactNumber: string,
    officeEmail: string,
    officeType: string,
    file: File | null;
    photoOffice: string;
    previewUrl: string;
  }>({
    id: "",
    locationId: "",
    officeId: "",
    officeName: "",
    officeAddress: "",
    latitude: "",
    longitude: "",
    region: "",
    province: "",
    city: "",
    district: "",
    managerName: "",
    contactNumber: "",
    officeEmail: "",
    officeType: "",
    file: null,
    photoOffice: "",
    previewUrl: "",
  });

  const [editErrors, setEditErrors] = useState({
    officeId: false,
    officeName: false,
    officeAddress: false,
    latitude: false,
    longitude: false,
    region: false,
    province: false,
    city: false,
    district: false,
    managerName: false,
    contactNumber: false,
    officeEmail: false,
    officeType: false,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (file: File) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Format tidak didukung",
        text: "Hanya file .png, .jpg, dan .jpeg yang diperbolehkan",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran file terlalu besar",
        text: "Ukuran maksimum adalah 5MB",
      });
      return;
    }

    setOfficeData({ ...officeData, file });
  };

  const handleEditFileUpload = (file) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Format tidak didukung",
        text: "Hanya file .png, .jpg, dan .jpeg yang diperbolehkan",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran file terlalu besar",
        text: "Ukuran maksimum adalah 5MB",
      });
      return;
    }

    setEditOfficeData({ ...editOfficeData, file });
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOfficeData({ ...officeData, [name]: value });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditOfficeData({ ...editOfficeData, [name]: value });
  };

  const validateForm = () => {
    let newErrors = {
      officeId: false,
      officeName: !officeData.officeName,
      officeAddress: !officeData.officeAddress,
      latitude: !officeData.latitude,
      longitude: !officeData.longitude,
      region: !officeData.region,
      province: !officeData.province,
      city: !officeData.city,
      district: !officeData.district,
      managerName: !officeData.managerName,
      contactNumber: !officeData.contactNumber,
      officeEmail: !officeData.officeEmail,
      officeType: !officeData.officeType,
    };

    setErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  const validateEditForm = () => {
    let newErrors = {
      officeId: false,
      officeName: !editOfficeData.officeName,
      officeAddress: !editOfficeData.officeAddress,
      latitude: !editOfficeData.latitude,
      longitude: !editOfficeData.longitude,
      region: !editOfficeData.region,
      province: !editOfficeData.province,
      city: !editOfficeData.city,
      district: !editOfficeData.district,
      managerName: !editOfficeData.managerName,
      contactNumber: !editOfficeData.contactNumber,
      officeEmail: !editOfficeData.officeEmail,
      officeType: !editOfficeData.officeType,
    };

    setEditErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  const errorResponse = (title, error) => {
    let errorTitle = "";
    let errorDetails = "";

    // Handle backend error response
    if (error.response?.data) {
      const backendError = error.response.data;

      // Debug log untuk melihat struktur error
      console.log(
        "Backend Error Structure:",
        JSON.stringify(backendError, null, 2)
      );

      // Main error message
      errorTitle = backendError.error || errorTitle;

      // Handle all possible email validation error formats - PERBAIKAN UTAMA
      const isEmailValidationError =
        // Case 1: Error in details (Sequelize style)
        (backendError.details &&
          /validation isEmail on (email|officeEmail)/i.test(
            backendError.details
          )) ||
        // Case 2: Error in errors array (Sequelize validation errors)
        (backendError.errors &&
          backendError.errors.some(
            (err: any) =>
              (err.path === "email" || err.path === "officeEmail") &&
              err.validatorKey === "isEmail"
          )) ||
        // Case 3: Error in message (generic API responses)
        (backendError.message &&
          /email.*invalid/i.test(backendError.message));

      if (isEmailValidationError) {
        errorTitle = "Format Email Tidak Valid";
        errorDetails =
          "• Format email tidak valid (contoh: nama@perusahaan.com)";
      }
      // Only use details if not an email error
      else if (backendError.details) {
        errorDetails = backendError.details;
      }

      // Handle validation errors (improved processing)
      if (backendError.errors) {
        const validationMessages = backendError.errors
          .map((err: any) => {
            // Handle both array and object error formats
            const errorObj = Array.isArray(err) ? err[0] : err;

            // Skip email errors since we already handled them
            if (
              (errorObj.path === "email" ||
                errorObj.path === "officeEmail") &&
              errorObj.validatorKey === "isEmail"
            ) {
              return null;
            }
            return `• ${errorObj.message || "Terjadi kesalahan validasi"}`;
          })
          .filter(Boolean) // Remove null entries
          .join("\n");

        if (validationMessages) {
          errorDetails = errorDetails
            ? `${errorDetails}\n${validationMessages}`
            : validationMessages;
        }
      }

      // Handle duplicate fields (improved formatting)
      if (backendError.duplicates) {
        const duplicateMessages = backendError.duplicates
          .map((dup: any) => `• ${dup.field}: ${dup.value} sudah terdaftar`)
          .join("\n");

        if (duplicateMessages) {
          errorDetails = errorDetails
            ? `${errorDetails}\n${duplicateMessages}`
            : duplicateMessages;
        }
      }
    }
    // Handle network or other errors
    else {
      errorDetails =
        error.message || "Terjadi kesalahan jaringan atau server";
    }

    // Show error in Swal (improved formatting)
    Swal.fire({
      icon: "error",
      title: title,
      html: `
                <div style="text-align: left;">
                  <p style="font-weight: bold; margin-bottom: 10px; color: #d32f2f;">
                    ${errorTitle}
                  </p>
                  ${errorDetails
          ? `<div style="
                          color: #5f2120;
                          background: #fdecea;
                          padding: 12px;
                          border-radius: 4px;
                          font-size: 0.9em;
                          border-left: 4px solid #d32f2f;
                          line-height: 1.5;
                        ">
                          ${errorDetails.replace(/\n/g, "<br>")}
                        </div>`
          : ""
        }
                </div>
              `,
      confirmButtonColor: "#0F62FE",
      width: "600px",
    });
  }

  // Fungsi untuk validasi file attachment
  const validateAttachmentFile = (file: File): boolean => {
    const validTypes = [
      "image/png", "image/jpeg", "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    const isValidType = validTypes.includes(file.type);
    const isValidSize = file.size <= maxSize;

    if (!isValidType) {
      Swal.fire({
        icon: "warning",
        title: "Format File Tidak Didukung",
        text: `File ${file.name} memiliki format yang tidak didukung. Format yang didukung: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG`,
      });
      return false;
    }

    if (!isValidSize) {
      Swal.fire({
        icon: "warning",
        title: "Ukuran File Terlalu Besar",
        text: `File ${file.name} melebihi batas ukuran 10MB`,
      });
      return false;
    }

    return true;
  };

  // Fungsi untuk handle file upload multiple - CREATE OFFICE
  const handleCreateAttachmentUpload = (files: FileList) => {
    const fileArray = Array.from(files);

    // Validasi jumlah file
    if (createAttachments.length + fileArray.length > 10) {
      Swal.fire({
        icon: "warning",
        title: "Batas File Terlampaui",
        text: "Maksimum 10 file diperbolehkan",
      });
      return;
    }

    const validFiles = fileArray.filter(validateAttachmentFile);

    if (validFiles.length > 0) {
      setCreateAttachments(prev => [...prev, ...validFiles]);
      console.log("Files added to create attachments:", validFiles);
    }
  };

  // Fungsi untuk handle file upload multiple - EDIT OFFICE
  const handleEditAttachmentUpload = (files: FileList) => {
    const fileArray = Array.from(files);

    // Validasi jumlah file total (existing + new)
    const totalFiles = existingAttachments.length + editAttachments.length + fileArray.length;
    if (totalFiles > 10) {
      Swal.fire({
        icon: "warning",
        title: "Batas File Terlampaui",
        text: `Total attachments tidak boleh lebih dari 10 file. Saat ini: ${existingAttachments.length} existing + ${editAttachments.length} baru`,
      });
      return;
    }

    const validFiles = fileArray.filter(validateAttachmentFile);

    if (validFiles.length > 0) {
      setEditAttachments(prev => [...prev, ...validFiles]);
      console.log("Files added to edit attachments:", validFiles);
    }
  };

  // Fungsi untuk menghapus file yang baru diupload (CREATE)
  const removeCreateAttachment = (index: number) => {
    if (index < 0 || index >= createAttachments.length) return;

    const fileToRemove = createAttachments[index];

    Swal.fire({
      title: 'Hapus File?',
      html: `
    <div style="text-align: left;">
      <p>Anda akan menghapus file:</p>
      <p><strong>${fileToRemove.name}</strong></p>
      <p>Ukuran: ${(fileToRemove.size / 1024 / 1024).toFixed(2)} MB</p>
    </div>
  `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    }).then((result) => {
      if (result.isConfirmed) {
        setCreateAttachments(prev => prev.filter((_, i) => i !== index));

        Swal.fire({
          icon: "success",
          title: "File Dihapus",
          text: "File berhasil dihapus dari daftar upload",
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  // Fungsi untuk menghapus file yang baru diupload (EDIT)
  const removeEditAttachment = (index: number) => {
    if (index < 0 || index >= editAttachments.length) return;

    const fileToRemove = editAttachments[index];

    Swal.fire({
      title: 'Hapus File?',
      html: `
    <div style="text-align: left;">
      <p>Anda akan menghapus file:</p>
      <p><strong>${fileToRemove.name}</strong></p>
      <p>Ukuran: ${(fileToRemove.size / 1024 / 1024).toFixed(2)} MB</p>
    </div>
  `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    }).then((result) => {
      if (result.isConfirmed) {
        setEditAttachments(prev => prev.filter((_, i) => i !== index));

        Swal.fire({
          icon: "success",
          title: "File Dihapus",
          text: "File berhasil dihapus dari daftar upload",
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  // Reset attachments
  const resetCreateAttachments = () => {
    setCreateAttachments([]);
    setCreateAttachmentDescription("");
    const fileInput = document.getElementById('create-attachment-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  // Hanya reset attachments baru, pertahankan existing attachments
  const resetEditAttachments = () => {
    setEditAttachments([]); // Hanya reset file baru
    setEditAttachmentDescription(""); // Hanya reset deskripsi baru
    // JANGAN reset existingAttachments di sini!
    const fileInput = document.getElementById('edit-attachment-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  // Fungsi untuk handle file upload multiple - DIPERBAIKI
  const handleAttachmentUpload = (files: FileList) => {
    const fileArray = Array.from(files);

    // Validasi jumlah file
    if (attachments.length + fileArray.length > 10) {
      Swal.fire({
        icon: "warning",
        title: "Batas File Terlampaui",
        text: "Maksimum 10 file diperbolehkan",
      });
      return;
    }

    // Validasi tipe file dan ukuran
    const validTypes = [
      "image/png", "image/jpeg", "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ];

    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = fileArray.filter(file => {
      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= maxSize;

      if (!isValidType) {
        console.warn(`File ${file.name} has invalid type: ${file.type}`);
      }
      if (!isValidSize) {
        console.warn(`File ${file.name} exceeds size limit: ${file.size} bytes`);
      }

      return isValidType && isValidSize;
    });

    const invalidFiles = fileArray.filter(file =>
      !validTypes.includes(file.type) || file.size > maxSize
    );

    if (invalidFiles.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "File Tidak Valid",
        html: `
        <div style="text-align: left;">
          <p>Beberapa file tidak memenuhi persyaratan:</p>
          <ul>
            ${invalidFiles.map(file =>
          `<li>${file.name} - ${file.type} - ${(file.size / 1024 / 1024).toFixed(2)}MB</li>`
        ).join('')}
          </ul>
          <p>Format yang didukung: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (maks 10MB)</p>
        </div>
      `,
      });
      return;
    }

    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
      console.log("Files added to attachments:", validFiles);
    }
  };

  // Fungsi untuk load existing attachments - DIPERBAIKI
  const loadExistingAttachments = async (officeId: string) => {
    try {
      console.log("Loading attachments for office:", officeId);
      const attachments = await getOfficeAttachments(officeId);
      console.log("Loaded attachments:", attachments);
      setExistingAttachments(attachments);

      // Reset hanya attachments baru, pertahankan existing
      setEditAttachments([]);
      setEditAttachmentDescription("");
    } catch (error) {
      console.error("Gagal memuat attachments:", error);
      Swal.fire({
        icon: "warning",
        title: "Gagal Memuat Attachments",
        text: "Data attachments tidak dapat dimuat, tetapi Anda tetap dapat melanjutkan edit office.",
        confirmButtonColor: "#0F62FE",
      });
    }
  };

  // Enhanced error handling untuk download
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      // Validation
      if (!attachment?.attachmentId) {
        throw new Error('Data attachment tidak valid');
      }

      Swal.fire({
        title: 'Mengunduh...',
        text: `Mempersiapkan "${attachment.originalName}"`,
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Timeout handling
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: Download terlalu lama')), 30000)
      );

      // Download dengan timeout
      await Promise.race([
        downloadAttachmentFile(attachment.attachmentId, attachment.originalName),
        timeoutPromise
      ]);

      Swal.close();

      // Success feedback
      Swal.fire({
        icon: 'success',
        title: 'Berhasil Diunduh',
        text: `File "${attachment.originalName}" telah diunduh`,
        timer: 1500,
        showConfirmButton: false
      });

    } catch (error: any) {
      Swal.close();

      let errorMessage = 'Gagal mengunduh file';
      if (error.message.includes('Timeout')) {
        errorMessage = 'Download timeout. Coba lagi.';
      } else if (error.message.includes('tidak ditemukan')) {
        errorMessage = 'File tidak ditemukan di server.';
      } else if (error.message.includes('akses')) {
        errorMessage = 'Anda tidak memiliki akses ke file ini.';
      } else {
        errorMessage = error.message || 'Terjadi kesalahan tidak diketahui';
      }

      Swal.fire({
        icon: 'error',
        title: 'Download Gagal',
        text: errorMessage,
        confirmButtonColor: '#0F62FE',
      });
    }
  };

  // Fungsi untuk menghapus file yang baru diupload (belum ke server) - IMPLEMENTASI LENGKAP
  const removeAttachment = (index: number) => {
    if (index < 0 || index >= attachments.length) {
      console.warn("Index attachment tidak valid:", index);
      return;
    }

    const fileToRemove = attachments[index];

    // Konfirmasi penghapusan
    Swal.fire({
      title: 'Hapus File?',
      html: `
      <div style="text-align: left;">
        <p>Anda akan menghapus file:</p>
        <p><strong>${fileToRemove.name}</strong></p>
        <p>Ukuran: ${(fileToRemove.size / 1024 / 1024).toFixed(2)} MB</p>
      </div>
    `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    }).then((result) => {
      if (result.isConfirmed) {
        // Hapus file dari state
        setAttachments(prev => {
          const newAttachments = prev.filter((_, i) => i !== index);
          console.log("File dihapus. Sisa attachments:", newAttachments);
          return newAttachments;
        });

        Swal.fire({
          icon: "success",
          title: "File Dihapus",
          text: "File berhasil dihapus dari daftar upload",
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  // Fungsi untuk menghapus existing attachment (sudah di server)
  const removeExistingAttachment = async (attachmentId: string) => {
    try {
      const attachmentToDelete = existingAttachments.find(att => att.attachmentId === attachmentId);

      if (!attachmentToDelete) {
        Swal.fire({
          icon: 'error',
          title: 'Data Tidak Ditemukan',
          text: 'File yang akan dihapus tidak ditemukan'
        });
        return;
      }

      const result = await Swal.fire({
        title: 'Hapus File Permanent?',
        html: `
      <div style="text-align: left;">
        <p>File yang dihapus tidak dapat dikembalikan:</p>
        <p><strong>${attachmentToDelete.originalName}</strong></p>
        <p>Ukuran: ${(attachmentToDelete.fileSize / 1024 / 1024).toFixed(2)} MB</p>
        <p>Upload: ${new Date(attachmentToDelete.createdAt).toLocaleDateString('id-ID')}</p>
      </div>
    `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ya, Hapus Permanent!',
        cancelButtonText: 'Batal'
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Menghapus File...',
          text: 'Sedang menghapus file dari server',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Panggil service delete
        await deleteAttachment(attachmentId);

        // Update state - hapus dari existingAttachments
        setExistingAttachments(prev => {
          const newAttachments = prev.filter(att => att.attachmentId !== attachmentId);
          console.log("Existing attachment dihapus. Sisa:", newAttachments);
          return newAttachments;
        });

        Swal.fire({
          icon: "success",
          title: "File Dihapus",
          text: "File berhasil dihapus permanent dari sistem",
          confirmButtonColor: "#0F62FE",
        });
      }
    } catch (error: any) {
      console.error("Error deleting attachment:", error);

      Swal.fire({
        icon: "error",
        title: "Gagal menghapus file",
        html: `
      <div style="text-align: left;">
        <p><strong>${error.response?.data?.message || "Terjadi kesalahan"}</strong></p>
        <p style="margin-top: 8px; color: #666;">
          Status: ${error.response?.status || 'Unknown'}<br>
          Silakan coba lagi atau hubungi administrator.
        </p>
      </div>
    `,
      });
    }
  };

  const handleSubmitAddOffice = async () => {
    if (validateForm()) {
      try {
        // Step 1: Create office
        const newOffice = await createOffice(officeData);
        const locData = {
          locationName: officeData.officeAddress,
          city: officeData.city,
          province: officeData.province,
          district: officeData.district,
          country: officeData.region,
          mapsLocation: officeData.longitude + ',' + officeData.latitude,
          longitude: officeData.longitude,
          latitude: officeData.latitude,
        }

        let newLocation;
        if (newOffice.officeId) {
          newLocation = await createLocation(locData);
        }

        let newOfficeLocation;
        if (newOffice.officeId && newLocation.locationId) {
          newOfficeLocation = await createOfficeLocation({ OfficeId: newOffice.officeId, LocationId: newLocation.locationId });
        }

        // Step 2: Upload photo if available
        if (officeData.file) {
          await uploadOfficePhoto(newOffice.officeId, officeData.file);
        }

        // Step 3: Upload attachments if available
        if (createAttachments.length > 0) {
          console.log("Uploading create attachments:", createAttachments);
          await uploadOfficeAttachments(newOffice.officeId, createAttachments, createAttachmentDescription);
        }

        Swal.fire({
          icon: "success",
          title: "Office berhasil ditambahkan",
          confirmButtonColor: "#0F62FE",
        });

        setOpenAddOfficeDialog(false);
        resetCreateAttachments();
        setOfficeData({
          officeId: "",
          officeName: "",
          officeAddress: "",
          latitude: "",
          longitude: "",
          region: "",
          province: "",
          city: "",
          district: "",
          managerName: "",
          contactNumber: "",
          officeEmail: "",
          officeType: "",
          file: null,
        });

        await fetchOffices();

      } catch (error: any) {
        console.error("Error in handleSubmitAddOffice:", error);
        let title = "Gagal membuat office";
        errorResponse(title, error);
      }
    } else {
      showPopupMandatoryError();
    }
  };

  // Reset attachments saat modal ditutup - DIPERBAIKI
  const resetAttachments = () => {
    console.log("Resetting attachments");
    setAttachments([]);
    setExistingAttachments([]);
    setAttachmentDescription("");

    // Clear file input
    const fileInput = document.getElementById('attachment-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';

    const editFileInput = document.getElementById('edit-attachment-upload') as HTMLInputElement;
    if (editFileInput) editFileInput.value = '';
  };

  const handleSubmitEditOffice = async () => {
    if (validateEditForm()) {
      try {
        const updatedOffice = await editOffice(editOfficeData);

        const locData = {
          locationId: editOfficeData.locationId,
          locationName: editOfficeData.officeAddress,
          city: editOfficeData.city,
          province: editOfficeData.province,
          district: editOfficeData.district,
          country: editOfficeData.region,
          mapsLocation: editOfficeData.longitude + ',' + editOfficeData.latitude,
          longitude: editOfficeData.longitude,
          latitude: editOfficeData.latitude,
        }

        let updatedLocation;
        if (updatedOffice.officeId) {
          updatedLocation = await editLocation(locData);
        }

        if (editOfficeData.file && editOfficeData.file.name !== originalEditOfficeData.photoOffice) {
          await uploadOfficePhoto(editOfficeData.officeId, editOfficeData.file);
        }

        // Upload new attachments if available
        if (editAttachments.length > 0) {
          console.log("Uploading edit attachments:", editAttachments);
          await uploadOfficeAttachments(editOfficeData.officeId, editAttachments, editAttachmentDescription);
        }

        Swal.fire({
          icon: "success",
          title: "Office berhasil diperbarui",
          confirmButtonColor: "#0F62FE",
        });

        await fetchOffices();
        setOpenEditOfficeDialog(false);
        setIsEditing(false);
        resetEditAttachments();

        setCurrentPage(1);
      } catch (error) {
        console.error("Error in handleSubmitEditOffice:", error);
        let title = "Gagal mengubah office";
        errorResponse(title, error);
      }
    } else {
      showPopupMandatoryError();
    }
  };

  const handleOpenEditOffice = async (office) => {
    const photoOffice = office.photoOffice;

    let previewUrl;
    if (photoOffice && typeof photoOffice === "string") {
      previewUrl = await fetchOfficePhotoBlob(photoOffice);
    }

    // Set data office
    await setEditOfficeData({
      ...office,
      locationId: office.Locations[0].locationId,
      city: office.Locations[0].city,
      province: office.Locations[0].province,
      district: office.Locations[0].district,
      region: office.region,
      longitude: office.Locations[0].longitude,
      latitude: office.Locations[0].latitude,
      file: null,
      photoOffice: office.photoOffice,
      previewUrl,
    });

    // Set original data untuk cancel
    await setOriginalEditOfficeData({
      ...office,
      locationId: office.Locations[0].locationId,
      city: office.Locations[0].city,
      province: office.Locations[0].province,
      district: office.Locations[0].district,
      region: office.region,
      longitude: office.Locations[0].longitude,
      latitude: office.Locations[0].latitude,
      file: null,
      photoOffice: office.photoOffice,
      previewUrl,
    });

    // Load existing attachments
    await loadExistingAttachments(office.officeId);

    await setIsEditing(false);
    await setOpenEditOfficeDialog(true);
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDeleteRows = async () => {
    try {
      await Promise.all(selectedRows.map(async (id) => {
        const deletedOffice = await deleteOffice(id);

      }));

      Swal.fire({
        icon: "success",
        title: "Office berhasil dihapus",
        confirmButtonColor: "#0F62FE",
      });

      setSelectedRows([]);
      fetchOffices(); // refresh data
      setOpenDeleteDialog(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Gagal menghapus item",
        text: error.response?.data?.message || "Terjadi kesalahan",
      });
    }
  };

  const handleCancelEditOffice = () => {
    setEditOfficeData(originalEditOfficeData); // Kembalikan nilai asli
    setIsEditing(false); // Kunci kembali form
    setOpenEditOfficeDialog(false);
  };

  // FILTER

  const handleFilterChange = (index: number, key: string, value: string) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;
    setFilters(newFilters);
  };

  const handleAddFilter = () => {
    setFilters([...filters, { field: "id", value: "" }]);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      // Buat object parameter pencarian sesuai `OfficeSearchParams`
      const searchParams: OfficeSearchParams = {};

      filters.forEach((filter) => {
        if (filter.value.trim() !== "") {
          searchParams[filter.field] = filter.value;
        }
      });

      // // Panggil fungsi pencarian dari itemServices.tsx
      // const response = await getOffices(searchParams);
      // console.log(response);
      // // Format hasil pencarian sebelum ditampilkan di tabel
      // handleSearchResults(response.data);

      setFilterParams(searchParams);
      setOpenFilterDialog(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal mengambil data",
        text: error.response?.data?.message || "Terjadi kesalahan",
      });
      handleCloseFilter();
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Tambahkan fungsi ini
  const handleSearchResults = (results) => {
    setRows(results); // Update rows agar sesuai dengan format tabel
    setOpenFilterDialog(false); // Tutup dialog setelah apply filter
  };

  const handleCloseFilter = () => {
    setOpenFilterDialog(false);
  };

  const handleResetFilters = () => {
    setFilters([{ field: "", value: "" }]); // Reset ke kondisi awal dengan satu filter kosong
  };

  //======================= Search ===================================
  const handleSearch = async () => {
    // if (searchQuery.trim() === "") return; // Hindari pencarian kosong
    setIsLoading(true);
    setError("");

    try {
      // const results = await getOffices({
      //   keyword: searchQuery,
      //   limit: rowsPerPage,
      //   offset,
      // });
      setFilterParams(
        {
          keyword: searchQuery
        }
      );
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Tampilkan loading
      Swal.fire({
        title: 'Preparing Export...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Opsi 1: Export semua data (dengan filter yang sama seperti tabel)
      const exportData = await getExportData();

      // Opsi 2: Export hanya data yang terlihat di tabel saat ini
      // const exportData = rows.map(formatEmployeeForExport);

      // Opsi 3: Export hanya data yang dipilih
      // if (selectedRows.length === 0) {
      //   Swal.fire({
      //     icon: 'warning',
      //     title: 'No Selection',
      //     text: 'Please select at least one employee to export',
      //   });
      //   return;
      // }
      // const exportData = rows
      //   .filter(row => selectedRows.includes(row.id))
      //   .map(formatEmployeeForExport);

      // Buat worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Buat workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Office");

      // Generate file Excel
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Offices_${date}.xlsx`);

      // Tutup loading
      Swal.close();

    } catch (error) {
      console.error('Export failed:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.message || 'Failed to export office data',
      });
    }
  };

  // Fungsi untuk mengambil data yang akan diexport
  const getExportData = async () => {
    const allOffices = await getOffices({
      ...filterParams,
      limit: 10000, // Ambil semua data
      offset: 0,
      orderBy: sortConfig.orderBy,
      order: sortConfig.order,
    });

    return allOffices.data.map(office => ({
      'Office Name': office.officeName,
      'Office Email': office.officeEmail,
      'Office Address': office.officeAddress,
      'Longitude': office.Locations[0].longitude,
      'Latitude': office.Locations[0].latitude,
      'City': office.Locations[0].city,
      'Province': office.Locations[0].province,
      'District': office.Locations[0].district,
      'Region': office.region,
      'Manager Name': office.managerName,
      'Office Contact': office.contactNumber,
      'Office Type': office.officeType,
    }));
  };

  return (
    isLoading ? <LoadingOverlay /> :
      <Box sx={{ padding: 3 }}>
        {/* Header & Tabs */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Office List
          </Typography>
        </Box>

        {/* Toolbar */}
        <Box className="toolbar">
          {/* Left Controls */}
          <Box className="leftControls">
            <Typography>Show</Typography>
            <Select value={rowsPerPage} onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1); // reset ke halaman 1 saat ganti limit
            }} size="small">
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>

            {/* Search Button & Input */}
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={() => setShowSearch((prev) => !prev)}
              className="searchButton"
            >
              Search
            </Button>
            {showSearch && (
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search"
                value={searchQuery}
                onChange={async (e) => {
                  await setSearchQuery(e.target.value);
                }}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()} // Pencarian dengan Enter
                autoFocus
                className="searchInput"
              />
            )}

            {/* Menampilkan Error */}
            {error && <p style={{ color: "red" }}>{error}</p>}
          </Box>

          {/* Right Controls */}
          <Box className="rightControls">
            <Button
              startIcon={<Delete />}
              variant="text"
              onClick={() => {
                const selectedData = rows.filter((row) => selectedRows.includes(row.officeId));
                setItemsToDelete(selectedData);
                setOpenDeleteDialog(true);
              }}
              disabled={selectedRows.length === 0}
            >
              Delete
            </Button>
            {/* Filter Button */}
            <Button
              startIcon={<FilterList />}
              variant="text"
              onClick={() => setOpenFilterDialog(true)}
            >
              Filters
            </Button>
            <Button startIcon={<Download />} variant="contained" className="exportButton" onClick={() => handleExport()}>
              Export
            </Button>
            <Button startIcon={<Add />} variant="contained" className="addItemDetailsButton" onClick={() => setOpenAddOfficeDialog(true)}>
              Add New Office
            </Button>
          </Box>
        </Box>


        {/* Delete Button & Subtitle */}

        <Box className="deleteContainer">
          <Typography variant="body2" className="detailsSubtitle">
            Details List Of All Offices
          </Typography>
        </Box>


        {/* Table */}
        <TableContainer component={Paper} className="tableContainer">
          <Table className="table">
            <TableHead className="tableHead">
              <TableRow>
                {/* Checkbox header */}
                <TableCell padding="checkbox" className="tableCell">
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                    checked={selectedRows.length === rows.length}
                    onChange={() =>
                      setSelectedRows(selectedRows.length === rows.length ? [] : rows.map((row) => row.officeId))
                    }
                  />
                </TableCell>
                <TableCell>No</TableCell>
                {/* Header dengan sorting */}
                <TableCell className="tableCell" onClick={() => handleSort("officeName")} style={{ cursor: "pointer" }}>
                  <strong>Office Name</strong>
                  {sortConfig.orderBy === "officeName" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </TableCell>
                <TableCell className="tableCell" onClick={() => handleSort("officeEmail")} style={{ cursor: "pointer" }}>
                  <strong>Office Email</strong>
                  {sortConfig.orderBy === "officeEmail" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </TableCell>
                <TableCell className="tableCell" onClick={() => handleSort("officeAddress")} style={{ cursor: "pointer" }}>
                  <strong>Office Address</strong>
                  {sortConfig.orderBy === "officeAddress" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </TableCell>
                <TableCell className="tableCell" onClick={() => handleSort("managerName")} style={{ cursor: "pointer" }}>
                  <strong>Manager Name</strong>
                  {sortConfig.orderBy === "managerName" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </TableCell>
                <TableCell className="tableCell" onClick={() => handleSort("contactNumber")} style={{ cursor: "pointer" }}>
                  <strong>Office Contact</strong>
                  {sortConfig.orderBy === "contactNumber" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </TableCell>
                <TableCell className="tableCell" onClick={() => handleSort("officeType")} style={{ cursor: "pointer" }}>
                  <strong>Office Type</strong>
                  {sortConfig.orderBy === "officeType" && (sortConfig.order === "asc" ? " ▲" : " ▼")}
                </TableCell>
                <TableCell className="tableCell">
                </TableCell> {/* Kolom untuk aksi */}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.officeId} selected={selectedRows.includes(row.officeId)}>
                  {/* Checkbox */}
                  <TableCell padding="checkbox" className="tableCell">
                    <Checkbox
                      checked={selectedRows.includes(row.officeId)}
                      onChange={() => handleSelectRow(row.officeId)}
                    />
                  </TableCell>
                  <TableCell>
                    {/* Nomor urut disesuaikan dengan pagination */}
                    {offset + index + 1}
                  </TableCell>
                  {/* Data */}
                  <TableCell className="tableCell" title={row.officeName}><strong>{row.officeName}</strong></TableCell>
                  <TableCell className="tableCell" title={row.officeEmail}>{row.officeEmail}</TableCell>
                  <TableCell className="tableCell" title={row.officeAddress}>{row.officeAddress}</TableCell>
                  <TableCell className="tableCell" title={row.managerName}>{row.managerName}</TableCell>
                  <TableCell className="tableCell" title={row.contactNumber}>{row.contactNumber}</TableCell>
                  {/* Status Badge */}
                  <TableCell className="tableCell" title={row.officeType}>
                    <Box className="statusBadge" style={{ backgroundColor: officeTypeColors[row.officeType] ? officeTypeColors[row.officeType] : "grey" }}>
                      {row.officeType}
                    </Box>
                  </TableCell>
                  {/* Ikon More Options */}
                  {/* Ikon More Options */}
                  <TableCell className="tableCell">
                    <IconButton onClick={() => handleOpenEditOffice(row)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>


        {/* Pagination */}
        <Box
          className="paginationContainer"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: "16px 0",
          }}
        >
          {/* Info jumlah data */}
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {`Showing ${offset + 1} to ${Math.min(
              offset + rowsPerPage,
              totalItems
            )} of ${totalItems} entries`}
          </Typography>

          {/* Komponen Pagination */}
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            className="pagination"
            showFirstButton
            showLastButton
            renderItem={(item) => (
              <PaginationItem
                {...item}
                components={{ first: FirstPage, last: LastPage }}
              />
            )}
          />
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Delete Rows?</DialogTitle>
          <DialogContent>
            <DialogContentText>Are you sure you want to delete the selected rows?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDeleteRows} color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* POPUP Add New Office */}
        <Dialog open={openAddOfficeDialog} onClose={() => { setOpenAddOfficeDialog(false); resetAttachments() }} fullWidth maxWidth="lg"
          sx={{ backdropFilter: "blur(5px)", borderRadius: "10px" }}>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">Add New Office</Typography>
              <IconButton onClick={() => setOpenAddOfficeDialog(false)} color="error"><Close /></IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
              {/* Left Side (Inputs & Map) */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth label="Input Office Name" name="officeName"
                    placeholder="Enter office name" value={officeData.officeName}
                    onChange={handleInputChange} error={errors.officeName}
                    helperText={errors.officeName ? "Mandatory Field" : ""}
                  />
                  <TextField
                    fullWidth label="Input Office Address" name="officeAddress"
                    placeholder="Enter office address" value={officeData.officeAddress}
                    onChange={handleInputChange} error={errors.officeAddress}
                    helperText={errors.officeAddress ? "Mandatory Field" : ""}
                  />
                </Box>

                {/* Map Placeholder */}
                <Box sx={{ width: "100%", height: "auto", bgcolor: "inherit", display: "flex", alignItems: "center", justifyContent: "center", my: 2 }}>
                  <MapPinLocation
                    latitude={officeData.latitude}
                    longitude={officeData.longitude}
                    onSelect={({ lat, lng }) => {
                      // if (isEditing) {
                      setOfficeData({ ...officeData, latitude: lat.toString(), longitude: lng.toString() });
                      // }
                      // else {
                      //   setOfficeData({ ...officeData, latitude: lat.toString(), longitude: lng.toString() });
                      // }
                    }}
                  />

                </Box>

                {/* Latitude & Longitude */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth label="Input Latitude" name="latitude"
                    placeholder="Enter latitude" value={officeData.latitude}
                    onChange={handleInputChange} error={errors.latitude}
                    helperText={errors.latitude ? "Mandatory Field" : ""}
                  />
                  <TextField
                    fullWidth label="Input Longitude" name="longitude"
                    placeholder="Enter longitude" value={officeData.longitude}
                    onChange={handleInputChange} error={errors.longitude}
                    helperText={errors.longitude ? "Mandatory Field" : ""}
                  />
                </Box>

                {/* File Upload */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    mt: 3
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    handleFileUpload(file);
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {officeData.file ? (
                    <img
                      src={URL.createObjectURL(officeData.file)}
                      alt="Preview"
                      style={{ width: "100%", height: "auto", borderRadius: "8px", objectFit: "contain", maxHeight: 180 }}
                    />
                  ) : (
                    <>
                      <PhotoCamera fontSize="large" />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Drag & drop or click to upload image
                      </Typography>
                    </>
                  )}
                  <input
                    hidden
                    accept="image/png, image/jpeg, image/jpg"
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (.png, .jpg, .jpeg)
                  </Typography>
                </Box>
              </Box>



              {/* Right Side (Dropdowns & Inputs) */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  select
                  fullWidth label="Input Office Region" name="region"
                  placeholder="Enter region" value={officeData.region}
                  onChange={handleInputChange} error={errors.region}
                  helperText={errors.region ? "Mandatory Field" : ""}
                >
                  <MenuItem value="">Select Region</MenuItem>
                  {regions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth label="Input Province" name="province"
                  placeholder="Enter province" value={officeData.province}
                  onChange={handleInputChange} error={errors.province}
                  helperText={errors.province ? "Mandatory Field" : ""}
                />
                <TextField
                  fullWidth label="Input City" name="city"
                  placeholder="Enter city" value={officeData.city}
                  onChange={handleInputChange} error={errors.city}
                  helperText={errors.city ? "Mandatory Field" : ""}
                />
                <TextField
                  fullWidth label="Input District" name="district"
                  placeholder="Enter district" value={officeData.district}
                  onChange={handleInputChange} error={errors.district}
                  helperText={errors.district ? "Mandatory Field" : ""}
                />
                <TextField
                  fullWidth label="Input Office Manager" name="managerName"
                  placeholder="Enter manager name" value={officeData.managerName}
                  onChange={handleInputChange} error={errors.managerName}
                  helperText={errors.managerName ? "Mandatory Field" : ""}
                />

                {/* Office Contact & Email */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth label="Input Office Contact Number" name="contactNumber"
                    placeholder="Enter contact number" value={officeData.contactNumber}
                    onChange={handleInputChange} error={errors.contactNumber}
                    helperText={errors.contactNumber ? "Mandatory Field" : ""}
                  />
                  <TextField
                    fullWidth label="Input Office Email" name="officeEmail"
                    placeholder="Enter email" value={officeData.officeEmail}
                    onChange={handleInputChange} error={errors.officeEmail}
                    helperText={errors.officeEmail ? "Mandatory Field" : ""}
                  />
                </Box>

                <TextField
                  select
                  fullWidth label="Input Office Type" name="officeType"
                  placeholder="Enter office type" value={officeData.officeType}
                  onChange={handleInputChange} error={errors.officeType}
                  helperText={errors.officeType ? "Mandatory Field" : ""}
                >
                  <MenuItem value="">Select Office Type</MenuItem>
                  {officeTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                {/* Attachments Section - CREATE OFFICE */}
                <Box sx={{ width: "100%", mt: 2 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Attachments
                    <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                      ({createAttachments.length}/10 files)
                    </Typography>
                  </Typography>

                  {/* File Upload Area */}
                  <Box
                    sx={{
                      border: "2px dashed #aaa",
                      padding: 3,
                      borderRadius: "12px",
                      backgroundColor: "#fafafa",
                      textAlign: "center",
                      cursor: "pointer",
                      mb: 2
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleCreateAttachmentUpload(e.dataTransfer.files);
                    }}
                    onClick={() => document.getElementById('create-attachment-upload')?.click()}
                  >
                    <CloudUploadIcon fontSize="large" />
                    <Typography variant="body2" color="textSecondary" mt={1}>
                      Drag & drop atau klik untuk upload file (maks 10 file, 10MB/file)
                    </Typography>
                    <Typography variant="caption" display="block" mt={1}>
                      Format yang didukung: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
                    </Typography>
                    <input
                      id="create-attachment-upload"
                      hidden
                      multiple
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      onChange={(e) => handleCreateAttachmentUpload(e.target.files)}
                    />
                  </Box>

                  {/* Description Input */}
                  <TextField
                    fullWidth
                    label="Deskripsi Attachments (Opsional)"
                    value={createAttachmentDescription}
                    onChange={(e) => setCreateAttachmentDescription(e.target.value)}
                    size="small"
                    sx={{ mb: 2 }}
                    placeholder="Masukkan deskripsi untuk semua file yang diupload"
                  />

                  {/* Uploaded Files List */}
                  {createAttachments.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        File yang akan diupload:
                      </Typography>
                      {createAttachments.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: "white"
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeCreateAttachment(index)}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions sx={{ justifyContent: "space-between", padding: 2 }}>
            <Button variant="outlined" color="error" sx={{ width: 120 }} onClick={() => setOpenAddOfficeDialog(false)}>CANCEL</Button>
            <Button variant="contained" color="success" sx={{ width: 120 }} onClick={handleSubmitAddOffice}>SAVE</Button>
          </DialogActions>
        </Dialog>

        {/* POPUP Edit Office */}
        <Dialog open={openEditOfficeDialog}
          onClose={() => {
            setOpenEditOfficeDialog(false);
            // Jangan reset existingAttachments saat close modal
            setEditAttachments([]);
            setEditAttachmentDescription("");
          }}
          fullWidth maxWidth="lg"
          sx={{ backdropFilter: "blur(5px)", borderRadius: "10px" }}>
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">Edit Office</Typography>
              <IconButton onClick={() => setOpenEditOfficeDialog(false)} color="error"><Close /></IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
              {/* Left Side (Inputs & Map) */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth label="Input Office Name" name="officeName"
                    placeholder="Enter office name" value={editOfficeData.officeName}
                    onChange={handleEditInputChange} error={editErrors.officeName}
                    helperText={editErrors.officeName ? "Mandatory Field" : ""}
                    disabled={!isEditing}
                  />
                  <TextField
                    fullWidth label="Input Office Address" name="officeAddress"
                    placeholder="Enter office address" value={editOfficeData.officeAddress}
                    onChange={handleEditInputChange} error={editErrors.officeAddress}
                    helperText={editErrors.officeAddress ? "Mandatory Field" : ""}
                    disabled={!isEditing}
                  />
                </Box>

                {/* Map Placeholder */}
                <Box sx={{ width: "100%", height: "auto", bgcolor: "inherit", display: "flex", alignItems: "center", justifyContent: "center", my: 2 }}>
                  <MapPinLocation
                    latitude={editOfficeData.latitude}
                    longitude={editOfficeData.longitude}
                    disabled={!isEditing}
                    onSelect={({ lat, lng }) => {
                      if (isEditing) {
                        setEditOfficeData({ ...editOfficeData, latitude: lat.toString(), longitude: lng.toString() });
                      }
                      // else {
                      //   setOfficeData({ ...officeData, latitude: lat.toString(), longitude: lng.toString() });
                      // }
                    }}
                  />

                </Box>

                {/* Latitude & Longitude */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField fullWidth label="Input Latitude" name="latitude"
                    placeholder="Enter latitude" value={editOfficeData.latitude ? editOfficeData.latitude : ""}
                    onChange={handleEditInputChange} error={editErrors.latitude}
                    helperText={editErrors.latitude ? "Mandatory Field" : ""}
                    disabled={!isEditing}
                  />
                  <TextField fullWidth label="Input Longitude" name="longitude"
                    placeholder="Enter longitude" value={editOfficeData.longitude ? editOfficeData.longitude : ""}
                    onChange={handleEditInputChange} error={editErrors.longitude}
                    helperText={editErrors.longitude ? "Mandatory Field" : ""}
                    disabled={!isEditing}
                  />
                </Box>
                {/* File Upload Section */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed #aaa",
                    padding: 4,
                    borderRadius: "12px",
                    backgroundColor: "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    position: "relative",
                    mt: 3
                  }}
                  disabled={!isEditing}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    if (isEditing) {
                      e.preventDefault();
                      const file = e.dataTransfer.files[0];
                      handleEditFileUpload(file);
                    }
                  }}
                  onClick={() => {
                    if (isEditing) {
                      editFileInputRef.current?.click()
                    }
                  }}
                >
                  {editOfficeData.file ? (
                    // Jika user baru upload gambar
                    <img
                      src={URL.createObjectURL(editOfficeData.file)}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                        objectFit: "contain",
                        maxHeight: 180,
                      }}
                    />
                  ) : editOfficeData.previewUrl ? (
                    // Jika sudah ada foto dari server
                    <img
                      src={editOfficeData.previewUrl}
                      alt="Existing"
                      style={{
                        width: "100%",
                        height: "auto",
                        borderRadius: "8px",
                        objectFit: "contain",
                        maxHeight: 180,
                      }}
                    />
                  ) : (
                    // Jika tidak ada gambar sama sekali
                    <>
                      <PhotoCamera fontSize="large" />
                      <Typography variant="body2" color="textSecondary" mt={1}>
                        Drag & drop or click to upload image
                      </Typography>
                    </>
                  )}

                  <input
                    hidden
                    accept="image/png, image/jpeg, image/jpg"
                    type="file"
                    ref={editFileInputRef}
                    onChange={(e) => handleEditFileUpload(e.target.files[0])}
                  />
                  <Typography variant="caption" mt={1}>
                    Max size: 5MB (.png, .jpg, .jpeg)
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  select
                  fullWidth label="Input Office Region" name="region"
                  placeholder="Enter region" value={editOfficeData.region}
                  onChange={handleEditInputChange} error={editErrors.region}
                  helperText={editErrors.region ? "Mandatory Field" : ""}
                  disabled={!isEditing}
                >
                  <MenuItem value="">Select Region</MenuItem>
                  {regions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth label="Input Province" name="province"
                  placeholder="Enter province" value={editOfficeData.province}
                  onChange={handleEditInputChange} error={editErrors.province}
                  helperText={editErrors.province ? "Mandatory Field" : ""}
                  disabled={!isEditing}
                />
                <TextField
                  fullWidth label="Input City" name="city"
                  placeholder="Enter city" value={editOfficeData.city}
                  onChange={handleEditInputChange} error={editErrors.city}
                  helperText={editErrors.city ? "Mandatory Field" : ""}
                  disabled={!isEditing}
                />
                <TextField
                  fullWidth label="Input District" name="district"
                  placeholder="Enter district" value={editOfficeData.district}
                  onChange={handleEditInputChange} error={editErrors.district}
                  helperText={editErrors.district ? "Mandatory Field" : ""}
                  disabled={!isEditing}
                />
                <TextField
                  fullWidth label="Input Office Manager" name="managerName"
                  placeholder="Enter manager name" value={editOfficeData.managerName}
                  onChange={handleEditInputChange} error={editErrors.managerName}
                  helperText={editErrors.managerName ? "Mandatory Field" : ""}
                  disabled={!isEditing}
                />

                {/* Office Contact & Email */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth label="Input Office Contact Number" name="contactNumber"
                    placeholder="Enter contact number" value={editOfficeData.contactNumber}
                    onChange={handleEditInputChange} error={editErrors.contactNumber}
                    helperText={editErrors.contactNumber ? "Mandatory Field" : ""}
                    disabled={!isEditing}
                  />
                  <TextField
                    fullWidth label="Input Office Email" name="officeEmail"
                    placeholder="Enter email" value={editOfficeData.officeEmail}
                    onChange={handleEditInputChange} error={editErrors.officeEmail}
                    helperText={editErrors.officeEmail ? "Mandatory Field" : ""}
                    disabled={!isEditing}
                  />
                </Box>

                <TextField
                  select
                  fullWidth label="Input Office Type" name="officeType"
                  placeholder="Enter office type" value={editOfficeData.officeType}
                  onChange={handleEditInputChange} error={editErrors.officeType}
                  helperText={editErrors.officeType ? "Mandatory Field" : ""}
                  disabled={!isEditing}
                >
                  <MenuItem value="">Select Office Type</MenuItem>
                  {officeTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                {/* Attachments Section - EDIT OFFICE */}
                <Box sx={{ width: "100%", mt: 2 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Attachments
                    <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                      (Total: {existingAttachments.length + editAttachments.length}/10 files)
                    </Typography>
                  </Typography>

                  {/* Existing Attachments - Always Visible */}
                  {existingAttachments.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        File Terupload ({existingAttachments.length}):
                      </Typography>
                      {existingAttachments.map((attachment) => (
                        <Box
                          key={attachment.attachmentId}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: "white"
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {attachment.originalName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {(attachment.fileSize / 1024 / 1024).toFixed(2)} MB •
                              {new Date(attachment.createdAt).toLocaleDateString('id-ID')}
                              {attachment.description && ` • ${attachment.description}`}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadAttachment(attachment)}
                              title="Download File"
                            >
                              <Download />
                            </IconButton>
                            {isEditing && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeExistingAttachment(attachment.attachmentId)}
                                title="Hapus File"
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* New File Upload Area (hanya muncul saat editing) */}
                  {isEditing && (
                    <>
                      <Box
                        sx={{
                          border: "2px dashed #aaa",
                          padding: 3,
                          borderRadius: "12px",
                          backgroundColor: "#fafafa",
                          textAlign: "center",
                          cursor: "pointer",
                          mb: 2
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleEditAttachmentUpload(e.dataTransfer.files);
                        }}
                        onClick={() => document.getElementById('edit-attachment-upload')?.click()}
                      >
                        <CloudUploadIcon fontSize="large" />
                        <Typography variant="body2" color="textSecondary" mt={1}>
                          Drag & drop atau klik untuk upload file tambahan
                        </Typography>
                        <Typography variant="caption" display="block" mt={1}>
                          Maksimum 10 file total ({existingAttachments.length + editAttachments.length}/10 digunakan)
                        </Typography>
                        <input
                          id="edit-attachment-upload"
                          hidden
                          multiple
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                          onChange={(e) => handleEditAttachmentUpload(e.target.files)}
                        />
                      </Box>

                      {/* Description Input untuk file baru */}
                      <TextField
                        fullWidth
                        label="Deskripsi Attachments Baru (Opsional)"
                        value={editAttachmentDescription}
                        onChange={(e) => setEditAttachmentDescription(e.target.value)}
                        size="small"
                        sx={{ mb: 2 }}
                        placeholder="Masukkan deskripsi untuk file baru yang diupload"
                      />

                      {/* New Uploaded Files List */}
                      {editAttachments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            File baru yang akan diupload ({editAttachments.length}):
                          </Typography>
                          {editAttachments.map((file, index) => (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: 2,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                mb: 1,
                                backgroundColor: "white"
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeEditAttachment(index)}
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions sx={{ justifyContent: "space-between", padding: 2 }}>
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)} variant="contained" color="primary" sx={{ width: 120 }}>
                EDIT
              </Button>
            ) : (
              <>
                <Button onClick={() => {
                  setIsEditing(false);
                  setEditOfficeData(originalEditOfficeData); // Kembalikan nilai asli form
                  resetEditAttachments(); // Hanya reset attachments baru, existing tetap
                }} variant="outlined" color="error" sx={{ width: 120 }}>
                  CANCEL
                </Button>
                <Button onClick={handleSubmitEditOffice} variant="contained" color="success" sx={{ width: 120 }}>
                  SAVE
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>




        {/* ======================= Filter Dialog ============================= */}
        <Dialog open={openFilterDialog} onClose={() => setOpenFilterDialog(false)}>
          <DialogTitle>Advanced Search</DialogTitle>
          <DialogContent>
            {filters.map((filter, index) => {
              const usedFields = filters.map((f, i) => i !== index && f.field).filter(Boolean);
              const availableOptions = availableFilters.filter((opt) => !usedFields.includes(opt.value));

              return (
                <Box key={index} display="flex" alignItems="center" gap={1} mb={2}>
                  <TextField
                    select
                    value={filter.field}
                    onChange={(e) => handleFilterChange(index, "field", e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1, minWidth: 160 }}
                  >
                    {availableOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {filter.field === "officeType" ? (
                    <TextField
                      select
                      label="Office Type"
                      variant="outlined"
                      size="small"
                      value={filter.value}
                      onChange={(e) =>
                        handleFilterChange(index, "value", e.target.value)
                      }
                      sx={{ mt: 1, minWidth: 160 }}
                    >
                      {officeTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      label="Value"
                      variant="outlined"
                      size="small"
                      value={filter.value}
                      onChange={(e) =>
                        handleFilterChange(index, "value", e.target.value)
                      }
                      sx={{ mt: 1 }}
                    />
                  )}

                  {index > 0 && (
                    <IconButton onClick={() => handleRemoveFilter(index)}>
                      <Remove />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            {filters.length < availableFilters.length && (
              <Button startIcon={<Add />} onClick={handleAddFilter}>
                Add Filter
              </Button>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleResetFilters}>Reset Filter</Button>
            <Button onClick={handleCloseFilter}>Cancel</Button>
            <Button variant="contained" onClick={handleApplyFilters} disabled={isLoading}>
              {isLoading ? "Loading..." : "Apply"}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
  );
};

export default OfficeListPage;
